/**
 * Order List Page
 *
 * Display customer orders in a card grid with filters and pagination.
 * Fetches from /api/v1/widget/:slug/orders
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useWidgetAuth } from '../../contexts/WidgetAuthContext';
import { WidgetApiClient } from '../../services/widget/WidgetApiClient';
import { WidgetOrderService } from '../../services/widget/WidgetOrderService';
import { OrderStatusBadge } from '../../components/widget/OrderStatusBadge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '../../components/ui/card';
import type { Order, OrderListResponse } from '../../types/widget/order';

// Consolidated status filters for UI
const uniqueStatusFilters = [
  { value: 'all' as const, label: 'All Orders' },
  { value: 'pending' as const, label: 'Pending' },
  { value: 'active' as const, label: 'Active' },
  { value: 'completed' as const, label: 'Completed' },
  { value: 'cancelled' as const, label: 'Cancelled' },
];

export function OrderList() {
  const { providerSlug } = useParams<{ providerSlug: string }>();
  const { token, isAuthenticated } = useWidgetAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'pending' | 'active' | 'completed' | 'cancelled'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pageSize = 20;

  // Initialize order service with memoization
  const orderService = useMemo(
    () => new WidgetOrderService(providerSlug!, new WidgetApiClient(() => token)),
    [providerSlug, token]
  );

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    if (!isAuthenticated) {
      navigate(`/widget/${providerSlug}/login`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Convert "active" filter to appropriate status
      const statusFilter =
        selectedFilter === 'active'
          ? undefined // Backend should handle "active" as a group
          : selectedFilter === 'all'
          ? undefined
          : selectedFilter;

      const response: OrderListResponse = await orderService.listOrders({
        status: statusFilter,
        page: currentPage,
        pageSize,
      });

      setOrders(response.orders);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, selectedFilter, currentPage, providerSlug, navigate, orderService]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Format date for display
  const formatDate = (isoDate: string) => {
    try {
      return new Date(isoDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  // Truncate address
  const truncateAddress = (address: { street: string; city: string; state: string }) => {
    return `${address.street}, ${address.city}, ${address.state}`;
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-64 bg-gray-200 rounded animate-pulse" />
          </div>

          {/* Filter skeleton */}
          <div className="flex gap-2 mb-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 w-24 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>

          {/* Card skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 w-32 bg-gray-200 rounded mb-2" />
                  <div className="h-4 w-24 bg-gray-200 rounded" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-4 w-full bg-gray-200 rounded" />
                    <div className="h-4 w-3/4 bg-gray-200 rounded" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <svg
              className="w-12 h-12 text-red-400 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              Failed to Load Orders
            </h3>
            <p className="text-red-700 mb-4">{error}</p>
            <Button onClick={fetchOrders}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
            <p className="text-gray-600 mt-2">Track and manage your dumpster orders</p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {uniqueStatusFilters.map((filter) => (
              <Button
                key={filter.value}
                variant={selectedFilter === filter.value ? 'default' : 'outline'}
                onClick={() => {
                  setSelectedFilter(filter.value);
                  setCurrentPage(1);
                }}
              >
                {filter.label}
              </Button>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-12 text-center">
            <svg
              className="w-16 h-16 text-blue-400 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No orders yet
            </h3>
            <p className="text-gray-600 mb-6">
              Order your first dumpster to get started!
            </p>
            <Link to={`/widget/${providerSlug}`}>
              <Button>Browse Services</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
          <p className="text-gray-600 mt-2">
            {total} {total === 1 ? 'order' : 'orders'} total
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {uniqueStatusFilters.map((filter) => (
            <Button
              key={filter.value}
              variant={selectedFilter === filter.value ? 'default' : 'outline'}
              onClick={() => {
                setSelectedFilter(filter.value);
                setCurrentPage(1);
              }}
              size="sm"
            >
              {filter.label}
            </Button>
          ))}
        </div>

        {/* Order Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {orders.map((order) => (
            <Card key={order.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Order #{order.orderNumber}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {order.containerSize} - {order.materialType}
                    </p>
                  </div>
                  <OrderStatusBadge status={order.status} />
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Address:</span>
                    <p className="text-gray-600 truncate">
                      {truncateAddress(order.serviceAddress)}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Requested Date:
                    </span>
                    <p className="text-gray-600">
                      {formatDate(order.requestedDeliveryDate)}
                    </p>
                  </div>
                </div>
              </CardContent>

              <CardFooter>
                <Link
                  to={`/widget/${providerSlug}/orders/${order.id}`}
                  className="w-full"
                >
                  <Button variant="outline" className="w-full">
                    View Details
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
